import React__default, { useRef, useEffect } from 'react';
import { I as IconButton } from './mediaQueries-6282b8d3.js';
import { c as faChevronLeftExports, f as faChevronRightExports } from './faFile-16ac2d40.js';
import { u as useStrings, c as classNames } from './responsive-b521ac2b.js';
import { b as Skeleton, c as createClassName } from './SkeletonContainer-3c6bc430.js';

const pageStart = 0;
function usePaginator(page, onPageChange, resetKey) {
    const lastResetKey = useRef();
    const resetKeyString = typeof resetKey === 'string' ? resetKey : resetKey === null || resetKey === void 0 ? void 0 : resetKey.join('|');
    useEffect(() => {
        // Reset to first page when key changes.
        if (resetKeyString !== undefined) {
            if (lastResetKey.current === undefined) {
                lastResetKey.current = resetKeyString;
            }
            else if (resetKeyString !== lastResetKey.current) {
                lastResetKey.current = resetKeyString;
                onPageChange(pageStart);
            }
        }
    }, [resetKeyString, onPageChange]);
    if (!page) {
        return null;
    }
    return {
        pageNumber: page.pageNumber,
        pageSize: page.pageSize,
        resultCount: page.resultCount,
        pageStart,
        goBack: () => {
            onPageChange(Math.max(pageStart, page.pageNumber - 1));
        },
        goForward: () => {
            const pageMax = Math.ceil(page.resultCount / page.pageSize) - 1;
            onPageChange(Math.min(pageMax, page.pageNumber + 1));
        }
    };
}

const Pagination = ({ paginator, hideEmptyComponent, isLoading, centerHorizontally }) => {
    var _a;
    const { pagination: strings } = useStrings();
    const min = 1;
    const max = (_a = paginator === null || paginator === void 0 ? void 0 : paginator.resultCount) !== null && _a !== void 0 ? _a : 0;
    const start = paginator ? ((paginator.pageNumber - paginator.pageStart) * paginator.pageSize) + 1 : 0;
    const end = paginator && start ? Math.min(paginator.resultCount, start + paginator.pageSize - 1) : 0;
    const showButtons = paginator ? paginator.resultCount > paginator.pageSize : false;
    const showText = start > 0 && end > 0 && max > 0;
    const shouldHideEmptyComponent = hideEmptyComponent ? !showButtons && !showText : false;
    if (shouldHideEmptyComponent) {
        return null;
    }
    return React__default.createElement("div", { className: classNames(className(), {
            [className('centerHorizontally')]: centerHorizontally
        }), "aria-live": isLoading !== undefined ? 'polite' : 'off', "aria-busy": isLoading },
        (isLoading || showText) && React__default.createElement("div", { className: className('text') }, isLoading ? React__default.createElement(Skeleton, { width: 80, height: 24 }) : strings.range(start, end, max)),
        !isLoading && showButtons && React__default.createElement(React__default.Fragment, null,
            React__default.createElement(IconButton, { icon: faChevronLeftExports.faChevronLeft, iconSize: "lg", title: strings.previousPage, disabled: !start || start <= min, onClick: paginator === null || paginator === void 0 ? void 0 : paginator.goBack }),
            React__default.createElement(IconButton, { icon: faChevronRightExports.faChevronRight, iconSize: "lg", title: strings.nextPage, disabled: !end || !max || end >= max, onClick: paginator === null || paginator === void 0 ? void 0 : paginator.goForward })));
};
const className = createClassName('Pagination');

const MobileTableCell = ({ primary, secondary, date, actions, before, children }) => {
    return (React__default.createElement("td", { className: "MobileTableCell" },
        React__default.createElement("div", { className: "MobileTableCell-container" },
            before && (React__default.createElement("div", { className: "MobileTableCell-before" }, before)),
            React__default.createElement("div", { className: "MobileTableCell-content" },
                date && React__default.createElement("div", { className: "MobileTableCell-date" }, date),
                primary && React__default.createElement("div", { className: "MobileTableCell-primary" }, primary),
                secondary && React__default.createElement("div", { className: "MobileTableCell-secondary" }, secondary),
                children),
            actions && React__default.createElement("div", { className: "MobileTableCell-actions" }, actions))));
};

export { MobileTableCell as M, Pagination as P, usePaginator as u };
